// FILE: src/features/seo-portal/components/trust-score-gauge.tsx
// VERSION: 1.0.0
// START_MODULE_CONTRACT:
// PURPOSE: [Радиальный SVG-индикатор Индекса Доверия OXPA для карточки ЧОП (/chop/[slug]). Круговой gauge с градиентной заливкой, анимированным счётчиком и tier-бейджем.]
// SCOPE: [seo_portal, chop_card, trust_score, brutalist_premium_ui]
// INPUT: [score: number 0-100, compact?: boolean]
// OUTPUT: [Client React-компонент TrustScoreGauge — drop-in для сайдбара карточки ЧОП]
// KEYWORDS: [DOMAIN(SEO): trust-score, reputation; CONCEPT(gauge): radial, svg, gradient, animation, tier-badge; UI: brutalist-premium; PATTERN(count-up): requestAnimationFrame, IntersectionObserver]
// LINKS: [USES_API(seo_companies.trust_score); REUSES(@oxpa/shared/lib/trust-utils.getTrustBadge); USED_IN(app/(portal)/chop/[slug]/page.tsx)]
// END_MODULE_CONTRACT
//
// START_RATIONALE:
// Q: Почему пороги tier = 30/20, а не 90/75 (как предлагалось в ТЗ)?
// A: Вся кодовая база (getTrustBadge, og-image route, rating, reestr-chop, company/[id])
//    использует единую шкалу 30/20 для доверия. Прототип chop-card.html в своём JS
//    (setTier) тоже использует v>=30/v>=20. Пороги 90/75 делали бы score 80/100 «БАЗОВЫМ»,
//    что противоречит данным. Мы переиспользуем getTrustBadge для единого языка бейджей.
// Q: Почему SVG нативно, а не библиотека (react-circular-progressbar)?
// A: Ponytail ступень 3 — нативная фича платформы (SVG + stroke-dashoffset). 0 зависимостей.
// Q: Почему useId().replace(/:/g,"")?
// A: React useId() возвращает ":r0:" — двоеточие невалидно в SVG id/url(#...) в ряде браузеров.
// END_RATIONALE
//
// START_CHANGE_SUMMARY:
// LAST_CHANGE: 1.0.0 — Создан компонент TrustScoreGauge (радиальный gauge + tier-бейдж)
// END_CHANGE_SUMMARY

"use client";

import React, { useEffect, useId, useRef, useState } from "react";
import { getTrustBadge } from "@oxpa/shared/lib/trust-utils";
import { cn } from "@oxpa/shared/lib/utils";

// # START_INTERFACE_TrustScoreGaugeProps
interface TrustScoreGaugeProps {
  /** Общий Индекс Доверия OXPA (0-100). null/NaN трактуются как 0. */
  score: number;
  /** Компактный режим: меньший размер, без подписи «из 100». */
  compact?: boolean;
}
// # END_INTERFACE_TrustScoreGaugeProps

// # START_INTERFACE_TierVisual
interface TierVisual {
  /** Текстовый лейбл тира («Высокий» / «Средний» / «Базовый»). */
  label: string;
  /** Tailwind-классы для бейджа тира (text/border/bg из getTrustBadge). */
  badgeClass: string;
  /** Цвет крупного числа (text-*). */
  numberClass: string;
  /** Цвета SVG-градиента для кольца [start, end]. */
  gradientStops: { a: string; b: string };
}
// # END_INTERFACE_TierVisual

// Геометрия кольца. r=42 → длина окружности 2π·42 ≈ 263.89.
// Совпадает с прототипом chop-card.html (stroke-dasharray="263.9").
const RADIUS = 42;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

/**
 * Разрешение визуального тира по score. Пороги 30/20 — единые для всего приложения
 * (см. @oxpa/shared/lib/trust-utils.getTrustBadge). Цвет кольца зависит от тира:
 * высокий → зелёно-бирюзовый градиент, средний → янтарно-оранжевый, базовый → серый.
 */
function resolveTier(score: number): TierVisual {
  const badge = getTrustBadge(score);
  if (score >= 30) {
    return {
      label: badge.label,
      badgeClass: badge.color,
      numberClass: "text-green-400",
      gradientStops: { a: "#22c55e", b: "#14b8a6" },
    };
  }
  if (score >= 20) {
    return {
      label: badge.label,
      badgeClass: badge.color,
      numberClass: "text-[hsl(var(--portal-warn))]",
      gradientStops: { a: "#f59e0b", b: "#f97316" },
    };
  }
  return {
    label: badge.label,
    badgeClass: badge.color,
    numberClass: "text-[hsl(var(--portal-text-muted))]",
    gradientStops: { a: "#9ca3af", b: "#6b7280" },
  };
}

// # START_FUNCTION_TrustScoreGauge
/**
 * Радиальный SVG-индикатор Индекса Доверия OXPA.
 *
 * Логика:
 * 1. На mount подписывается на IntersectionObserver — анимация запускается, когда
 *    gauge попадает в viewport (как в прототипе chop-card.html).
 * 2. При срабатывании: stroke-dashoffset переходит к целевому значению через CSS
 *    transition (1.2s), а число счётчика анимируется requestAnimationFrame (cubic
 *    ease-out) в течение 1.4s.
 * 3. При prefers-reduced-motion — мгновенная установка финальных значений.
 */
export function TrustScoreGauge({ score, compact = false }: TrustScoreGaugeProps) {
  // useId() → ":r0:"; двоеточие невалидно в SVG id/url(), вырезаем.
  const gradientId = `tg-${useId().replace(/:/g, "")}`;

  const safeScore = Math.max(0, Math.min(100, Number.isFinite(score) ? score : 0));
  const tier = resolveTier(safeScore);
  const targetOffset = CIRCUMFERENCE - (safeScore / 100) * CIRCUMFERENCE;

  const [displayScore, setDisplayScore] = useState(0);
  const [offset, setOffset] = useState(CIRCUMFERENCE);
  const containerRef = useRef<HTMLDivElement>(null);

  // START_BLOCK_animateIn: запуск анимации при появлении в viewport
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

    const run = () => {
      // Кольцо анимируется CSS-переходом на stroke-dashoffset.
      setOffset(targetOffset);

      if (prefersReduced) {
        setDisplayScore(safeScore);
        return;
      }

      // Count-up числа (cubic ease-out, 1.4s) — как в прототипе.
      const duration = 1400;
      const startTime = performance.now();
      const tick = (now: number) => {
        const progress = Math.min((now - startTime) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        setDisplayScore(Math.round(eased * safeScore));
        if (progress < 1) {
          requestAnimationFrame(tick);
        } else {
          setDisplayScore(safeScore);
        }
      };
      requestAnimationFrame(tick);
    };

    // Ponytail: нативный IntersectionObserver (ступень 3). Без него — сразу.
    if (typeof IntersectionObserver === "undefined" || prefersReduced) {
      run();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          run();
          observer.disconnect();
        }
      },
      { threshold: 0.4 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [safeScore, targetOffset]);
  // END_BLOCK_animateIn

  const sizeClass = compact ? "w-28 h-28" : "w-[170px] h-[170px]";
  const numberTextSize = compact ? "text-3xl" : "text-[44px]";

  return (
    <section
      aria-label="Индекс доверия OXPA"
      className="border-2 border-[hsl(var(--portal-border-hover))] bg-[hsl(var(--portal-bg))]/40 backdrop-blur-sm p-6"
    >
      <div className="font-mono text-[11px] uppercase tracking-[0.14em] text-green-400">
        Индекс доверия OXPA
      </div>

      {/* START_BLOCK_radial: SVG-кольцо + центральное число */}
      <div ref={containerRef} className={cn("relative mx-auto my-4", sizeClass)}>
        <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90" aria-hidden="true">
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor={tier.gradientStops.a} />
              <stop offset="1" stopColor={tier.gradientStops.b} />
            </linearGradient>
          </defs>
          {/* Трек (фон) */}
          <circle
            cx="50"
            cy="50"
            r={RADIUS}
            fill="none"
            stroke="currentColor"
            className="text-[hsl(var(--portal-text-dim))]"
            strokeWidth="11"
          />
          {/* Заполнение с градиентом */}
          <circle
            cx="50"
            cy="50"
            r={RADIUS}
            fill="none"
            stroke={`url(#${gradientId})`}
            strokeWidth="11"
            strokeLinecap="round"
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={offset}
            style={{
              transition: "stroke-dashoffset 1.2s cubic-bezier(0.2,0.7,0.2,1)",
            }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <b
            className={cn(
              "font-mono font-extrabold leading-none tracking-tight tabular-nums",
              numberTextSize,
              tier.numberClass,
            )}
          >
            {displayScore}
          </b>
          {!compact && (
            <small className="mt-1 font-mono text-[10px] uppercase tracking-[0.12em] text-[hsl(var(--portal-text-muted))]">
              из 100
            </small>
          )}
        </div>
      </div>
      {/* END_BLOCK_radial */}

      {/* Tier-бейдж: ВЫСОКИЙ / СРЕДНИЙ / БАЗОВЫЙ */}
      <span
        className={cn(
          "block mt-3 text-center font-mono text-xs font-bold uppercase tracking-[0.12em] border-2 py-2",
          tier.badgeClass,
        )}
      >
        {tier.label}
      </span>
    </section>
  );
}
// # END_FUNCTION_TrustScoreGauge
